import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "../../components/Product-zone/ThemeContext";
import { LangProvider, useLang } from "../../components/Product-zone/LangContext";
import { SUBMENUS } from "../../components/Product-zone/zonesdata";

import TopBar from "../../components/Product-zone/TopBar";
import AppBar from "../../components/Product-zone/AppBar";
import LeftRail from "../../components/Product-zone/LeftRail";
import FeedZone from "../../components/Product-zone/FeedZone";
import RightRail from "../../components/Product-zone/RightRail";
import ComingSoonModal from "../../components/common/ComingSoonModal";

function Shell() {
  const { t } = useLang();
  const navigate = useNavigate();

  // ─── Default Zone/Submenu ───────────────────────────────
  const defaultZone = localStorage.getItem("defaultZone") || "tools";
  const defaultSubmenuLabel = localStorage.getItem("defaultSubmenu") || "TradingView";
  const defaultSubmenu =
    SUBMENUS[defaultZone]?.find((s) => s.label === defaultSubmenuLabel) ||
    SUBMENUS[defaultZone]?.[0];

  // ─── Core Zone States ───────────────────────────────
  const [activeZone, setActiveZone] = useState(defaultZone);
  const [activeSub, setActiveSub] = useState(defaultSubmenu);

  // ─── Layout Controls ───────────────────────────────
  const [splitMode, setSplitMode] = useState(1);
  const [showAppBar, setShowAppBar] = useState(true);
  const [orientation, setOrientation] = useState("vertical");
  const [fitScreen, setFitScreen] = useState(true);

  // ─── Modal State ───────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [isInvestor, setIsInvestor] = useState(
    localStorage.getItem("investorAccess") === "true"
  );

  const handleModalClose = () => setShowModal(false);

  // 🟢 Check URL for ?auth=true and trigger modal if not logged in
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auth = params.get("auth");
    const zone = params.get("zone") || "tools";
    const sub = params.get("sub") || "TradingView";

    setActiveZone(zone);
    setActiveSub({
      parent: { label: "Charts" },
      child: { label: sub },
    });

    if (auth === "true" && !localStorage.getItem("investorAccess")) {
      setShowModal(true); // 🔹 Show modal only if not logged in yet
    }
  }, []);

  // 🟢 Watch for login status (after password entered)
  useEffect(() => {
    const access = localStorage.getItem("investorAccess") === "true";
    setIsInvestor(access);
    if (access) setShowModal(false); // hide modal
  }, [showModal]);

  // ─── Layout constants ───────────────────────────────
  const TOPBAR_HEIGHT = 32;
  const APPBAR_HEIGHT = showAppBar ? 42 : 0;
  const TOTAL_HEADER = TOPBAR_HEIGHT + APPBAR_HEIGHT;

  // ─── FeedZone panes ───────────────────────────────
  const [activePane, setActivePane] = useState(0);
  const [paneContent, setPaneContent] = useState(Array(4).fill(null));
  const [paneProviders, setPaneProviders] = useState(Array(4).fill(null));

  // ─── Submenu + Zone Handlers ───────────────────────────────
  const handleSubmenuSelect = (item) => setActiveSub(item);

  const handleAppBarSelect = (zoneKey) => {
    setActiveZone(zoneKey);
    const access = localStorage.getItem("investorAccess") === "true";
    if (!access) setShowModal(true);
  };

  // 🟣 Optional: logout for testing/demo
  const handleLogout = () => {
    localStorage.removeItem("investorAccess");
    setIsInvestor(false);
    setShowModal(true);
  };

  return (
    <div className="h-screen w-screen overflow-hidden text-slate-900 dark:text-white relative">
      {/* ───── TopBar ───── */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: `${TOPBAR_HEIGHT}px`,
          zIndex: 50,
        }}
      >
        <TopBar
          splitMode={splitMode}
          setSplitMode={setSplitMode}
          showAppBar={showAppBar}
          setShowAppBar={setShowAppBar}
          orientation={orientation}
          setOrientation={setOrientation}
          fitScreen={fitScreen}
          setFitScreen={setFitScreen}
        />
      </div>

      {/* ───── AppBar ───── */}
      {showAppBar && (
        <div
          style={{
            position: "fixed",
            top: `${TOPBAR_HEIGHT}px`,
            left: 0,
            right: 0,
            height: `${APPBAR_HEIGHT}px`,
            zIndex: 40,
            background: "#0f172a",
            borderBottom: "1px solid #1e293b",
          }}
        >
          <AppBar activeZone={activeZone} onSelect={handleAppBarSelect} theme="dark" />
        </div>
      )}

      {/* ───── Main Layout (Left, Feed, Right) ───── */}
      <div
        className="flex w-full"
        style={{
          position: "fixed",
          top: `${TOTAL_HEADER}px`,
          left: 0,
          right: 0,
          height: `calc(100vh - ${TOTAL_HEADER}px)`,
        }}
      >
        {/* LEFT RAIL */}
        <LeftRail
          currentZoneKey={activeZone}
          onSubmenuSelect={handleSubmenuSelect}
          showAppBar={showAppBar}
        />

        {/* FEED ZONE */}
        <div className="relative flex-1">
          <FeedZone
            activeZone={activeZone}
            splitMode={splitMode}
            fitScreen={fitScreen}
            orientation={orientation}
            activeSub={activeSub}
            paneContent={paneContent}
            setPaneContent={setPaneContent}
            paneProviders={paneProviders}
            setPaneProviders={setPaneProviders}
            activePane={activePane}
            setActivePane={setActivePane}
            showAppBar={showAppBar}
          />

          {/* 🔹 Transparent overlay only if modal open */}
          {showModal && (
            <div className="absolute inset-0 bg-black/15 backdrop-blur-[2px] z-30 pointer-events-none"></div>
          )}
        </div>

        {/* RIGHT RAIL */}
        <RightRail t={t} />
      </div>

      {/* 🟣 Logout Button (Testing only) */}
      {isInvestor && (
        <button
          onClick={handleLogout}
          className="fixed bottom-1 left-6 z-[9999] text-xs bg-red-900 hover:bg-red-700 text-white px-3 py-1 rounded-md shadow-lg"
        >
          Logout Investor
        </button>
      )}

      {/* 🟡 Coming Soon Modal */}
      {showModal && <ComingSoonModal open={showModal} onClose={handleModalClose} />}
    </div>
  );
}


export default function ProductZone() {
  return (
    <ThemeProvider>
      <LangProvider>
        <Shell />
      </LangProvider>
    </ThemeProvider>
  );
}